import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { ArrowRight, Sparkles, Globe, Shield } from 'lucide-react';
import WaitlistCapture from './WaitlistCapture';

// English hero — AI-powered RFP platform illustration (EN)
const HERO_IMG_EN = 'https://customer-assets.emergentagent.com/job_3885f60a-e4ce-4e7e-b71d-89edd5337d92/artifacts/gxtwh5jw_Gemini_Generated_Image_42yylq42yylq42yy.jpeg';
// French hero — Illustration plateforme AO bilingue propulsée par IA (FR)
const HERO_IMG_FR = 'https://customer-assets.emergentagent.com/job_3885f60a-e4ce-4e7e-b71d-89edd5337d92/artifacts/1fmrywjz_Gemini_Generated_Image_4z65s4z65s4z65s4.jpeg';

const HERO_ALT = {
  en: 'ProposAI AI-powered RFP platform — smarter procurement for Canadian businesses. Showing AI RFP Creator, Vendor Intelligence panel, and bilingual Decision Panel.',
  fr: "ProposAI plateforme d'appel d'offres propulsée par l'IA — approvisionnement plus intelligent pour les entreprises canadiennes. Créateur de RFP IA, renseignement fournisseur et tableau de décision bilingue.",
};

const CAPABILITY_BADGES = [
  { icon: Sparkles, key: 'aiPowered' },
  { icon: Globe, key: 'bilingual' },
  { icon: Shield, key: 'compliance' },
];

export default function LandingHero({ onRegisterClick, onRegisterAsBusinessClick, onRegisterAsVendorClick }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'fr' ? 'fr' : 'en';
  const heroImg = lang === 'fr' ? HERO_IMG_FR : HERO_IMG_EN;

  const handleBusiness = onRegisterAsBusinessClick || onRegisterClick;
  const handleVendor = onRegisterAsVendorClick || onRegisterClick;

  return (
    <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">

          {/* ── Left: copy ───────────────────────────────────────── */}
          <div className="space-y-8">
            <div className="inline-flex items-center space-x-2 bg-secondary text-primary px-4 py-2 rounded-full text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              <span>{t('landing.hero.badge')}</span>
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
              {t('landing.hero.title')}
              <span className="block mt-2 text-primary">
                {t('landing.hero.titleAccent')}
              </span>
            </h1>

            <p className="text-xl text-slate-600 leading-relaxed">
              {t('landing.hero.description')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={handleBusiness}
                className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-stripe-md text-lg px-8 py-6 group"
                data-testid="hero-post-rfp-btn"
              >
                {t('landing.hero.ctaPostRfp')}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleVendor}
                className="border-2 border-border text-foreground hover:border-primary hover:text-primary text-lg px-8 py-6 group"
                data-testid="hero-find-contracts-btn"
              >
                {t('landing.hero.ctaFindContracts')}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            {/* Honest capability badges */}
            <div className="flex flex-wrap gap-3 pt-2">
              {CAPABILITY_BADGES.map(({ icon: Icon, key }) => (
                <div
                  key={key}
                  className="inline-flex items-center gap-2 bg-secondary/60 text-primary border border-primary/20 px-3 py-1.5 rounded-full text-sm font-medium"
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{t(`landing.trust.stats.${key}`)}</span>
                </div>
              ))}
            </div>

            {/* Waitlist capture — for visitors not ready to sign up */}
            <div className="pt-2 max-w-md">
              <WaitlistCapture variant="light" source="hero" />
            </div>
          </div>

          {/* ── Right: Custom illustration ─────────────────────── */}
          <div className="relative">
            <div className="absolute inset-0 bg-primary rounded-3xl blur-3xl opacity-10 animate-pulse" />
            <img
              src={heroImg}
              alt={HERO_ALT[lang]}
              className="relative rounded-2xl shadow-2xl w-full object-cover"
              loading="eager"
              fetchpriority="high"
            />
          </div>

        </div>
      </div>
    </section>
  );
}
